import { isEqual } from 'lodash';
import { ChangeEvent, ChangeEventHandler, FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { deepSet } from 'logic/deepSet';
import { deepDelete } from 'logic/deepDelete';

export type Options = {
    undefinedFalse: boolean;
    nullValue?: string; // When specified, watches for this value in handleChange and replaces it with `null` in the form data
    deleteOnValue?: string; // When specified, watches for this value in handleChange and removes values when matched
    
    // Set to true to debug issues with form data. Activating debug mode will cause events to
    // be logged to the console.
    debug?: boolean;

    // When specified, the form will be reset to initialData anytime these dependencies change
    // (by default, resets when initialData changes). But sometimes (M1-1843) data will change
    // unexpectedly and we want to force it to stay the same... e.g. use an object ID rather
    // than the default of an object reference.
    resetOn?: any[];
};

export function useForm<T>(formName: string, initialData: T, onSubmit?: (data: T) => void, options: Partial<Options> = {}) {
    const [ capturedInitialData, setCapturedInitialData ] = useState(initialData);

    // Store a ref to the data so the handleSubmit handler can be a constant reference
    const [ data, setData ] = useState(capturedInitialData);

    const dirty = !isEqual(data, capturedInitialData);

    const undo = useCallback(() => {
        setData(capturedInitialData);
    }, [ setData, capturedInitialData ]);

    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, checked, value, valueAsNumber, type } = e.currentTarget as HTMLInputElement;
        setData(data => {
            // Check if the data should be deleted
            if (options.deleteOnValue && value === options.deleteOnValue) {
                if (options.debug) {
                    console.log(`useForm[${formName}] ${name} new value is undefined`);
                }
                return deepDelete(data, name);
            }
            // Check if the data should be treated as null
            if (options.nullValue !== undefined && value === options.nullValue) {
                if (options.debug) {
                    console.log(`useForm[${formName}] ${name} new value is null`);
                }
                return deepSet(data, name, null);
            }

            let effectiveValue: string | number | boolean | undefined = value;
            switch (type) {
                case 'checkbox':
                    effectiveValue =
                        options.undefinedFalse && !checked
                            ? undefined
                            : checked;
                    break;

                case 'number':
                case 'date':
                case 'time':
                    effectiveValue = valueAsNumber || Number(value) || 0;
                    break;
            }
            if (options.debug) {
                console.log(`useForm[${formName}] ${name} new value is ${effectiveValue}`);
            }
            return deepSet(data, name, effectiveValue);
        });
    }, [ setData, options.debug, options.undefinedFalse, options.nullValue, options.deleteOnValue ]);

    //
    // Submit handler for convenience; to prevent default
    // form handling by the browser
    //
    const handleSubmit = useCallback((e: FormEvent<HTMLFormElement>) => {
        e.stopPropagation();
        e.preventDefault();
        if (onSubmit) {
            setData(data => {
                if (options.debug) {
                    console.log(`useForm[${formName}] submitting`, data);
                }
                onSubmit(data);
                return data;
            });
        }
        return false;
    }, [ setData, onSubmit ]);

    //
    // Update our internal data when the input data changes
    // (unless we were overridden and told to depend on the `resetOn` list)
    //
    useEffect(() => {
        if (options.debug) {
            console.log(`useForm[${formName}] resetting to`, initialData);
        }
        setCapturedInitialData(initialData);
        setData(initialData);
    }, [ ...(options.resetOn || [ initialData ]), setData, options.debug ]);

    return {
        data,
        dirty,
        setData,
        handleChange,
        handleSubmit,
        undo,
    };
}

//
// Some components (especially custom components) may return a value
// directly rather than a ChangeEvent<T>.  Use this helper hook when
// in those cases.  Note: `handleChange` comes from useForm() above.
//
// e.g.
// const handleChangeColor = useValueChangeHandler(handleChange, 'properties.color');
// const handleChangeSize = useValueChangeHandler<number>(handleChange, 'properties.size');
//
export function useValueChangeHandler<V = string, T = V, E extends HTMLElement = HTMLInputElement>(handleChange: ChangeEventHandler<E>, name: string, unmap?: (v: V) => T) {
    return useCallback((value: V) => {
        const t = unmap ? unmap(value) : value as unknown as T;
        handleChange({
            currentTarget: {
                name,
                type: typeof t === 'number' ? 'number' : 'text',
                value: t,
                valueAsNumber: typeof t === 'number' ? t : undefined,
            },
        } as unknown as ChangeEvent<E>);
    }, [ handleChange, name ]);
}
